import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { API_URL, TENANT_ID } from "../../config";
import { getMessage } from "../../data/locales";
import PasswordRequirements from "../../components/PasswordRequirements";
import { isStrongPassword } from "../../utils/passwordPolicy";
import AuthScreenLayout from "../../components/AuthScreenLayout";
import "./ResetPassword.css";

async function readResetLinkError(response) {
  const fallback = getMessage("resetPassword.invalidDefault");
  try {
    const data = await response.json();
    if (data && typeof data.error === "string" && data.error.trim()) {
      return data.error;
    }
  } catch {
    // A non-JSON 400 still means the link cannot be used.
  }
  return fallback;
}

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tokenStatus, setTokenStatus] = useState("checking");
  const [invalidMessage, setInvalidMessage] = useState("");

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setInvalidMessage(getMessage("resetPassword.invalidDefault"));
      setTokenStatus("invalid");
      return undefined;
    }

    let cancelled = false;

    const validateLink = async () => {
      try {
        const response = await fetch(`${API_URL}/reset-password/${encodeURIComponent(token)}`, {
          method: "GET",
          headers: {
            "x-tenant-id": TENANT_ID,
          },
        });

        if (cancelled) {
          return;
        }

        if (response.status === 400) {
          const linkError = await readResetLinkError(response);
          if (cancelled) {
            return;
          }
          setInvalidMessage(linkError);
          setTokenStatus("invalid");
          return;
        }

        // 5xx and any other non-400 status keep the form. The POST still checks the link.
        setTokenStatus("valid");
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setTokenStatus("valid");
        }
      }
    };

    validateLink();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError(null);

    if(!token) {
        setError("El enlace es inválido o ha expirado.");
        return;
    }

    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    if (!isStrongPassword(password)) {
        setError(getMessage("passwordPolicy.invalid"));
        return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/reset-password/${encodeURIComponent(token)}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-tenant-id": TENANT_ID,
        },
        body: JSON.stringify({ password, confirmPassword }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.mensaje);
        // Redirect after a few seconds
        timeoutRef.current = setTimeout(() => {
            navigate("/login");
        }, 3000);
      } else {
        setError(data.error || "El enlace es inválido o ha expirado.");
      }
    } catch (err) {
      console.error(err);
      setError("Error de conexión. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  if (message) {
      return (
        <AuthScreenLayout>
            <div className="auth-screen-card">
                <h2>¡Contraseña Restablecida!</h2>
                <div className="auth-screen-message success">{message}</div>
                <p>Serás redirigido al inicio de sesión en unos segundos...</p>
                <Link to="/login" className="auth-screen-button">
                    Ir al Inicio de Sesión
                </Link>
            </div>
        </AuthScreenLayout>
      );
  }

  if (tokenStatus === "checking") {
    return (
      <AuthScreenLayout>
        <div className="auth-screen-card">
          <div className="auth-screen-status" role="status">
            <div className="auth-screen-spinner" aria-hidden="true" />
            <p>{getMessage("resetPassword.checking")}</p>
          </div>
        </div>
      </AuthScreenLayout>
    );
  }

  if (tokenStatus === "invalid") {
    return (
      <AuthScreenLayout>
        <div className="auth-screen-card">
          <h2>{getMessage("resetPassword.invalidTitle")}</h2>
          <div className="auth-screen-message error" role="alert">
            {invalidMessage}
          </div>
          <div className="auth-screen-links">
            <Link to="/recuperar-password">{getMessage("resetPassword.requestNewLink")}</Link>
            <Link to="/login">{getMessage("resetPassword.goToLogin")}</Link>
          </div>
        </div>
      </AuthScreenLayout>
    );
  }

  return (
    <AuthScreenLayout>
      <div className="auth-screen-card">
        <h2>Restablecer Contraseña</h2>
        
        {error && <div className="auth-screen-message error">{error}</div>}

        <form onSubmit={handleSubmit} className="reset-password-form">
          <div className="reset-password-input-group">
            <label htmlFor="reset-password-new">{getMessage("resetPassword.newPassword")}</label>
            <div className="password-input-wrapper">
              <input
                id="reset-password-new"
                type={showPassword ? "text" : "password"}
                className="reset-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword((visible) => !visible)}
                aria-label={
                  showPassword
                    ? getMessage("resetPassword.hidePassword")
                    : getMessage("resetPassword.showPassword")
                }
                aria-pressed={showPassword}
              >
                {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
              </button>
            </div>
          </div>

          <PasswordRequirements />

          <div className="reset-password-input-group">
            <label htmlFor="reset-password-confirm">{getMessage("resetPassword.confirmPassword")}</label>
            <div className="password-input-wrapper">
              <input
                id="reset-password-confirm"
                type={showConfirmPassword ? "text" : "password"}
                className="reset-password-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowConfirmPassword((visible) => !visible)}
                aria-label={
                  showConfirmPassword
                    ? getMessage("resetPassword.hideConfirm")
                    : getMessage("resetPassword.showConfirm")
                }
                aria-pressed={showConfirmPassword}
              >
                {showConfirmPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
              </button>
            </div>
          </div>

          <button type="submit" className="auth-screen-button" disabled={loading}>
            {loading ? "Restableciendo..." : "Cambiar Contraseña"}
          </button>
        </form>
      </div>
    </AuthScreenLayout>
  );
};

export default ResetPassword;
